import { Component, Input, Output, EventEmitter, signal, OnInit, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UtcDateTimePipe } from '../../../../../pipes/utc-date-time.pipe';
import { DropdownStateService } from '../../../../../services/dropdown-state.service';

export interface VersionOption {
  record_id: string;
  version: string | null;
  status: string;
  timestampLabel: string;
  DraftVisibleToAll: boolean;
  UpdateHistory: {
    PublishedBy?: string;
    PublishedOn?: string;
    PublishedById?: string;
    UpdatedBy?: string;
    UpdatedAt?: string;
    UpdateType: string;
    UpdatedById?: string;
  };
  ReadyForRelease: boolean;
  ReadyForPublish: boolean;
  Production: boolean;
}

@Component({
  selector: 'app-version-dropdown',
  standalone: true,
  imports: [CommonModule, UtcDateTimePipe],
  templateUrl: './version-dropdown.component.html',
  styleUrl: './version-dropdown.component.scss'
})
export class VersionDropdownComponent implements OnInit, OnChanges {
  @Input() contentList: VersionOption[] = [];
  @Input() selectedValue: VersionOption | null = null;
  @Input() placeholder: string = 'Select';
  @Input() disabled: boolean = false;
  @Input() dropdownId: string = 'version-dropdown';
  @Output() onItemSelect = new EventEmitter<VersionOption>();

  selectedDisplayText = signal<string>('');

  constructor(private dropdownStateService: DropdownStateService) {}

  ngOnInit() {
    this.updateSelectedDisplayText();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedValue'] || changes['placeholder']) {
      this.updateSelectedDisplayText();
    }
  }

  get isOpen(): boolean {
    return this.dropdownStateService.isDropdownOpen(this.dropdownId);
  }

  toggleDropdown() {
    if (!this.disabled) {
      if (this.isOpen) {
        this.dropdownStateService.closeDropdown();
      } else {
        this.dropdownStateService.openDropdown(this.dropdownId);
      }
    }
  }

  selectItem(item: VersionOption) {
    this.selectedValue = item;
    this.onItemSelect.emit(item);
    this.dropdownStateService.closeDropdown();
    this.updateSelectedDisplayText();
  }

  closeDropdown() {
    this.dropdownStateService.closeDropdown();
  }

  trackByRecordId(index: number, item: VersionOption): string {
    return item.record_id;
  }

  private updateSelectedDisplayText() {
    if (this.selectedValue) {
      this.selectedDisplayText.set(this.getVersionDisplayText(this.selectedValue));
    } else {
      this.selectedDisplayText.set(this.placeholder);
    }
  }

  getVersionDisplayText(version: VersionOption): string {
    if (version.version) {
      return `Version ${version.version}`;
    } else if (version.status === 'draft') {
      if (!version.DraftVisibleToAll) {
        return 'Draft';
      } else {
        return 'Draft in Review';
      }
    }
    return 'Unknown';
  }

  getStatusTag(version: VersionOption): { text: string; class: string } | null {
    if (version.status === 'published') {
      return { text: 'Published', class: 'published-tag' };
    }
    return null;
  }

  getTimestamp(version: VersionOption): string {
    if (version.UpdateHistory?.PublishedOn) {
      return version.UpdateHistory.PublishedOn;
    } else if (version.UpdateHistory?.UpdatedAt) {
      return version.UpdateHistory.UpdatedAt;
    }
    return '';
  }

  getAuthor(version: VersionOption): string {
    if (version.UpdateHistory?.PublishedBy) {
      return version.UpdateHistory.PublishedBy;
    } else if (version.UpdateHistory?.UpdatedBy) {
      return version.UpdateHistory.UpdatedBy;
    }
    return '';
  }

  getAuthorId(version: VersionOption): string {
    if (version.UpdateHistory.PublishedById) {
      return version.UpdateHistory.PublishedById;
    } else if (version.UpdateHistory.UpdatedById) {
      return version.UpdateHistory.UpdatedById;
    }
    return '';
  }

  onOutsideClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.version-dropdown')) {
      this.closeDropdown();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.version-dropdown')) {
      this.closeDropdown();
    }
  }
}
